<script setup lang="ts">
import { useIntersectionObserver, useMediaQuery, useResizeObserver } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DEMO_CV_DATA } from '~/constants/demoCv'
import { useScrollReveal } from '~/composables/useScrollReveal'
import type { BusinessWebTemplate, CVTemplate } from '~/types'
import type { CVData } from '~/types/cv'

const { absUrl } = useSiteUrl()
const { t } = useI18n()
const { stats: publicStats, formatCount } = usePublicStats()

/*
 * `layout: false` + explicit NuxtLayout: the marketing frame without the
 * default layout's back link, as on every other Sofax inner page.
 */
definePageMeta({
  layout: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/ukazka', de: '/demo', pl: '/demo' } })

/*
 * Fade-up motion for the bands below the studio. `refresh()` runs after the
 * preview swaps, so anything that only exists behind one tab still animates in.
 */
const { refresh: refreshReveal } = useScrollReveal()

// Two formats: the CV PDF (print templates) and the web CV (web templates)
const mainTab = ref<'pdf' | 'web'>('pdf')

/*
 * Both pickers are the ones the editor uses, so the demo offers exactly the
 * templates a visitor gets after signing up — including Studio, which is the
 * default there as well.
 */
const selectedWebTemplate = ref<BusinessWebTemplate>('signature')

/** The address bar of the mock browser window; the classic layout is the card. */
const previewUrl = computed(() =>
  `inmyresume.com/${selectedWebTemplate.value === 'classic' ? 'card' : 'cv'}/`
)

// PDF download state
const isDownloading = ref(false)

// Template selector pro download
const selectedTemplate = ref<CVTemplate>('modern')

/**
 * A4 (794px at 96 DPI) plus the gutter the PDF preview keeps either side —
 * the width at which it stops scaling the sheet down.
 */
const FULL_WIDTH = 826

/**
 * Below this the sheet is scaled enough to be worth a zoom toggle. A stage a
 * few pixels short of A4 shrinks it by a percent or two, which the toggle
 * cannot meaningfully undo — so it stays hidden there.
 */
const ZOOM_THRESHOLD = 760

/**
 * The width the web layouts are laid out at inside the browser frame. They are
 * built for a desktop viewport and Tailwind's breakpoints answer to the real
 * window, not to the column they sit in — handed ~800px they kept the desktop
 * grid and the name ran over the experience panel. So they get a full desktop
 * width and the frame scales the result down instead.
 */
const DESKTOP_WIDTH = 1280

const stageEl = ref<HTMLElement | null>(null)
const stageWidth = ref(0)

useResizeObserver(stageEl, (entries) => {
  stageWidth.value = entries[0]?.contentRect.width ?? 0
})

/**
 * The preview scales itself to whatever width it is given, which on a phone
 * means an unreadable sheet. "100%" holds it at full size and lets the stage
 * scroll sideways instead; on a screen wide enough for both, the toggle would
 * do nothing, so it is not rendered.
 */
const zoom = ref<'fit' | 'full'>('fit')
const canZoom = computed(() => stageWidth.value > 0 && stageWidth.value < ZOOM_THRESHOLD)

/*
 * `zoom` rather than a transform: it rescales layout, so the frame keeps the
 * scaled height and scrolls normally instead of needing a measured wrapper.
 */
const webZoom = computed(() => {
  if (!stageWidth.value || !isDesktopViewport.value) return 1
  return Math.min(stageWidth.value / DESKTOP_WIDTH, 1)
})

/*
 * The breakpoints answer to the window, so below `lg` the layouts are already
 * in their mobile/tablet form. Forcing those onto the 1280px canvas and
 * zooming it down left a phone-sized layout stretched wide at a quarter of its
 * size; there the layout simply takes the stage's width at full size instead.
 */
const isDesktopViewport = useMediaQuery('(min-width: 1024px)')
const webCanvasStyle = computed(() =>
  isDesktopViewport.value
    ? { width: `${DESKTOP_WIDTH}px`, zoom: webZoom.value }
    : { width: '100%' },
)

// Demo CV data — the shared sample the server renders for showcase previews
const demoCVData = ref<CVData>({ ...DEMO_CV_DATA })

const experiencesList = computed(() => demoCVData.value.experiences || [])
const educationList = computed(() => demoCVData.value.education || [])

/** Feature cards under the studio; the icons come from the Bootstrap set. */
const features = [
  { id: 'twoFormats', icon: 'bi-palette-fill' },
  { id: 'pdfExport', icon: 'bi-file-earmark-pdf-fill' },
  { id: 'easyEditing', icon: 'bi-pencil-square' },
  { id: 'qrCodes', icon: 'bi-qr-code-scan' },
  { id: 'onlineSharing', icon: 'bi-share-fill' },
  { id: 'darkMode', icon: 'bi-lightbulb-fill' },
] as const

/** Counter band. A tile without a live figure is dropped rather than shown as 0. */
const statTiles = computed(() =>
  [
    { id: 'cvs', value: formatCount(publicStats.value?.cvs), label: t('common.demo.stats.cvsCreated') },
    { id: 'users', value: formatCount(publicStats.value?.users), label: t('common.demo.stats.users') },
    { id: 'templates', value: '3', label: t('common.demo.stats.templates') },
    { id: 'time', value: t('common.demo.stats.avgTimeValue'), label: t('common.demo.stats.avgTime') },
  ].filter(tile => tile.value),
)

const marqueeItems = computed(() => [
  t('common.demo.marquee.one'),
  t('common.demo.marquee.two'),
  t('common.demo.marquee.three'),
])

// Download PDF function - stahuje statické PDF soubory
async function downloadPDF() {
  try {
    isDownloading.value = true

    // Simulujeme krátké zpoždění pro "export"
    await new Promise(resolve => setTimeout(resolve, 800))

    const filename = `cv-demo-${selectedTemplate.value}-page.pdf`
    const pdfUrl = `/${filename}`

    // Vytvoříme link pro stažení
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = filename
    link.target = '_blank'

    // Přidáme do DOM, klikneme a odstraníme
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    return true
  } catch (error) {
    console.error('Error downloading PDF:', error)
    return false
  } finally {
    isDownloading.value = false
  }
}

// Anything that only exists behind one tab still has to be handed to the observer.
watch([mainTab, selectedWebTemplate], () => {
  refreshReveal()
})

/*
 * The floating CTA follows the visitor down the studio but steps aside for the
 * closing call to action, so the two are never on screen together.
 */
const pageEndEl = ref<HTMLElement | null>(null)
const showFloatingCta = ref(true)
useIntersectionObserver(pageEndEl, ([entry]) => {
  showFloatingCta.value = !entry?.isIntersecting
})

// SEO Meta
useSeoMeta({
  title: () => t('seo.demo.title'),
  description: () => t('seo.demo.description'),
  ogTitle: () => t('seo.demo.title'),
  ogDescription: () => t('seo.demo.description'),
  ogImage: () => absUrl('/og/home.png?v=2'),
  twitterCard: 'summary_large_image',
})
</script>

<template>
  <NuxtLayout name="sell">
    <!-- The band carries the crumb trail; the page keeps its own h1 below. -->
    <SofaxBreadcrumb :title="t('common.demo.title')" :items="[{ name: t('common.demo.title') }]" as="div" />

    <!-- Intro: centred opener with the template's doodles beside it -->
    <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
      <div class="pb-10 pt-12 md:pb-14 md:pt-16">
        <div class="shell relative">
          <img
            src="/sofax/content-shape-1.svg"
            alt=""
            aria-hidden="true"
            width="125"
            height="137"
            class="demo-doodle pointer-events-none absolute right-4 top-0 z-0 hidden w-16 select-none lg:block xl:w-20"
          >
          <img
            src="/sofax/background-shape-2.svg"
            alt=""
            aria-hidden="true"
            width="174"
            height="171"
            class="up-down-40 pointer-events-none absolute left-2 top-6 z-0 hidden w-20 select-none opacity-70 xl:block"
          >

          <div class="relative z-10 mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">
              <i class="bi bi-play-circle-fill text-accent" aria-hidden="true" />
              {{ t('common.demo.eyebrow') }}
            </div>

            <h1 class="!text-4xl md:!text-5xl xl:!text-6xl">{{ t('common.demo.headline') }}</h1>
            <p class="lead mt-5">{{ t('common.demo.intro') }}</p>

            <ul class="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-base text-body dark:text-gray-300">
              <li v-for="key in ['templates', 'web', 'export']" :key="key" class="inline-flex items-center gap-2">
                <i class="bi bi-check-circle-fill text-accent" aria-hidden="true" />
                {{ t(`common.demo.highlights.${key}`) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Studio: the controls and the live stage, full width -->
    <section class="sofax surface-alt relative overflow-hidden">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-5xl">
            <div class="min-w-0">
              <!--
                Controls laid out like the editor's preview panel: the format on
                the left, the template thumbnails next to it, the export on the
                right.
              -->
              <div class="panel !p-5 md:!p-6">
                <div class="flex flex-col gap-5">
                  <div class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                    <div class="flex min-w-0 flex-col gap-4">
                      <div class="flex flex-wrap gap-2" role="tablist">
                        <button
                          type="button"
                          role="tab"
                          class="tab-button !px-6 !py-2.5 !text-sm"
                          :class="{ active: mainTab === 'pdf' }"
                          :aria-selected="mainTab === 'pdf'"
                          @click="mainTab = 'pdf'"
                        >
                          <i class="bi bi-file-earmark-pdf mr-2" aria-hidden="true" />{{ t('common.demo.cvPdf') }}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          class="tab-button !px-6 !py-2.5 !text-sm"
                          :class="{ active: mainTab === 'web' }"
                          :aria-selected="mainTab === 'web'"
                          @click="mainTab = 'web'"
                        >
                          <i class="bi bi-globe2 mr-2" aria-hidden="true" />{{ t('common.demo.webCV') }}
                        </button>
                      </div>

                      <!-- The editor's own pickers: thumbnails on desktop, a
                           native select on a phone. -->
                      <div>
                        <CVTemplateSelector
                          v-if="mainTab === 'pdf'"
                          v-model="selectedTemplate"
                          :compact="true"
                          class="hidden md:inline-flex"
                        />
                        <CVBusinessWebTemplateSelector
                          v-else
                          v-model="selectedWebTemplate"
                          :compact="true"
                          class="hidden md:inline-flex"
                        />

                        <div class="md:hidden">
                          <CVTemplateSelector v-if="mainTab === 'pdf'" v-model="selectedTemplate" :description="true" />
                          <CVBusinessWebTemplateSelector v-else v-model="selectedWebTemplate" :description="true" />
                        </div>
                      </div>
                    </div>

                    <!-- Export action (only for CV PDF) -->
                    <SofaxBtn
                      v-if="mainTab === 'pdf'"
                      variant="dark"
                      class="btn-sm !w-full sm:!w-auto"
                      :label="isDownloading ? t('common.demo.exporting') : t('common.demo.exportPDF')"
                      :disabled="isDownloading"
                      @click="downloadPDF"
                    />
                  </div>

                </div>
              </div>

              <!-- Stage -->
              <div class="mt-6 overflow-hidden rounded-[0.625rem] border border-line bg-white dark:border-gray-700 dark:bg-gray-800">
                <!-- Stage toolbar -->
                <div class="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 dark:border-gray-700">
                  <span class="inline-flex items-center gap-2 font-title !text-sm font-semibold !text-ink dark:!text-white">
                    <span class="live-dot" aria-hidden="true" />
                    {{ t('common.demo.stage.label') }}
                  </span>

                  <span
                    v-if="mainTab === 'pdf'"
                    class="hidden rounded-full bg-surface px-3 py-1 !text-xs font-semibold !text-muted sm:inline-flex dark:bg-gray-700 dark:!text-gray-300"
                  >
                    {{ t('common.demo.stage.a4') }}
                  </span>

                  <!-- Zoom, only where fitting the column actually shrank the sheet -->
                  <div
                    v-if="mainTab === 'pdf' && canZoom"
                    class="ml-auto inline-flex items-center gap-1 rounded-full bg-surface p-1 dark:bg-gray-700"
                    role="group"
                    :aria-label="t('common.demo.stage.zoomLabel')"
                  >
                    <button
                      v-for="mode in (['fit', 'full'] as const)"
                      :key="mode"
                      type="button"
                      class="rounded-full px-3 py-1 !text-xs font-semibold transition-colors"
                      :class="zoom === mode
                        ? 'bg-white !text-ink shadow-sm dark:bg-gray-900 dark:!text-white'
                        : '!text-muted hover:!text-ink dark:!text-gray-400 dark:hover:!text-white'"
                      :aria-pressed="zoom === mode"
                      @click="zoom = mode"
                    >
                      {{ mode === 'fit' ? t('common.demo.stage.zoomFit') : t('common.demo.stage.zoomFull') }}
                    </button>
                  </div>
                </div>

                <!-- Canvas -->
                <!--
                  The sheet sits on the dot-grid desk with room around it; the
                  web preview fills the stage edge to edge, like a real window.
                -->
                <div
                  ref="stageEl"
                  class="dot-grid bg-surface text-line dark:bg-gray-900 dark:text-gray-700"
                  :class="mainTab === 'pdf' ? 'p-4 md:p-8' : ''"
                >
                  <Transition name="stage" mode="out-in">
                    <!-- Web layouts, framed in a browser window -->
                    <div v-if="mainTab === 'web'" :key="selectedWebTemplate" class="w-full">
                      <div class="bg-white dark:bg-gray-800">
                        <div class="flex items-center gap-3 border-b border-line bg-gray-50 px-3 py-2 md:px-4 md:py-2.5 dark:border-gray-700 dark:bg-gray-900">
                          <div class="flex shrink-0 gap-1.5" aria-hidden="true">
                            <span class="size-2 rounded-full bg-red-400 md:size-2.5" />
                            <span class="size-2 rounded-full bg-yellow-400 md:size-2.5" />
                            <span class="size-2 rounded-full bg-green-400 md:size-2.5" />
                          </div>
                          <div
                            class="mx-auto flex min-w-0 max-w-sm flex-1 items-center justify-center gap-1.5 truncate rounded-md bg-white px-3 py-1 !text-[0.625rem] !text-muted md:!text-xs dark:bg-gray-800 dark:!text-gray-400"
                          >
                            <i class="bi bi-lock-fill text-green-600 dark:text-green-400" aria-hidden="true" />
                            <span class="truncate">{{ previewUrl }}<span class="font-semibold !text-ink dark:!text-white">{{ t('common.demo.stage.sampleSlug') }}</span></span>
                          </div>
                          <span class="w-8 shrink-0 md:w-10" aria-hidden="true" />
                        </div>

                        <div>
                          <div :style="webCanvasStyle">
                            <div v-if="selectedWebTemplate === 'classic'" class="bg-gray-50 dark:bg-gray-900">
                              <CVBusinessCard
                                :cv-data="demoCVData" :experiences-list="experiencesList" :education-list="educationList"
                                :show-q-r-code="true" />
                            </div>
                            <div v-else-if="selectedWebTemplate === 'modern'" class="bg-white">
                              <CVBusinessWebTemplate
                                :cv-data="demoCVData" :experiences-list="experiencesList" :education-list="educationList"
                                :show-q-r-code="true" />
                            </div>
                            <div v-else-if="selectedWebTemplate === 'signature'" class="bg-white">
                              <CVBusinessWebSignature
                                :cv-data="demoCVData" :experiences-list="experiencesList" :education-list="educationList"
                                :show-q-r-code="true" />
                            </div>
                            <div v-else class="bg-white">
                              <CVBusinessWebSofax
                                :cv-data="demoCVData" :experiences-list="experiencesList" :education-list="educationList"
                                :show-q-r-code="true" animated />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!--
                      A4 sheets. The PDF preview fits itself to whatever width
                      it is handed and stacks every page it produces, so the page
                      only decides how wide that is and caps the stack on small
                      screens, where the CTA below has to stay in reach.
                    -->
                    <div v-else key="cv" class="preview-container">
                      <div
                        class="mx-auto w-full"
                        :class="zoom === 'full' && canZoom ? 'shrink-0' : ''"
                        :style="zoom === 'full' && canZoom
                          ? { width: `${FULL_WIDTH}px`, maxWidth: 'none' }
                          : { maxWidth: `${FULL_WIDTH}px` }"
                      >
                        <div class="max-h-[70svh] overflow-y-auto lg:max-h-none lg:overflow-visible">
                          <!-- `sample`: the showcase sells the template, so the
                               server renders its own sample CV without the
                               free-plan watermark. -->
                          <CVPreviewWrapper
                            :cv-data="demoCVData" :experiences-list="experiencesList"
                            :education-list="educationList" :show-q-r-code="true"
                            :template="selectedTemplate" frame="border" sample />
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>

              <p class="mt-4 text-center !text-sm !text-muted dark:!text-gray-400">
                <i class="bi bi-info-circle mr-1.5" aria-hidden="true" />{{ t('common.demo.stage.sampleData') }}
              </p>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- Features, out of the sidebar and into a proper grid -->
    <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell relative">
          <SofaxSectionHeading
            :eyebrow="t('common.demo.features.eyebrow')"
            :title="t('common.demo.features.title')"
            :text="t('common.demo.features.subtitle')"
          />

          <ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <li
              v-for="(feature, index) in features"
              :key="feature.id"
              data-reveal
              :style="{ '--reveal-delay': `${index * 70}ms` }"
            >
              <div class="card card-hover h-full !p-7">
                <SofaxIconTile :icon="feature.icon" size="md" class="mb-6" />
                <div class="mb-2 font-title text-xl font-bold text-ink dark:text-white">
                  {{ t(`common.demo.features.${feature.id}`) }}
                </div>
                <p class="text-base">{{ t(`common.demo.features.${feature.id}Desc`) }}</p>
              </div>
            </li>
          </ul>

          <img
            src="/sofax/background-shape-6.svg"
            alt=""
            aria-hidden="true"
            width="174"
            height="171"
            class="up-down-40 pointer-events-none absolute -right-10 bottom-0 -z-10 hidden w-28 opacity-60 2xl:inline-block"
          >
        </div>
      </div>
    </section>

    <!-- Counters, ticking up as the band scrolls in -->
    <section class="sofax surface-alt">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto mb-12 max-w-2xl text-center" data-reveal>
            <h2 class="!text-3xl md:!text-4xl">{{ t('common.demo.stats.title') }}</h2>
            <p class="mt-4">{{ t('common.demo.stats.subtitle') }}</p>
          </div>

          <dl class="grid gap-8 text-center" :class="statTiles.length === 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'">
            <div
              v-for="(tile, index) in statTiles"
              :key="tile.id"
              data-reveal
              :style="{ '--reveal-delay': `${index * 90}ms` }"
            >
              <dd class="font-title text-4xl font-bold -tracking-[1px] text-ink md:text-5xl dark:text-white">
                <SofaxCountUp :value="tile.value" />
              </dd>
              <dt class="mt-2 text-sm text-muted dark:text-gray-400">{{ tile.label }}</dt>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <SofaxTextSlider :items="marqueeItems" />

    <div ref="pageEndEl">
      <SofaxPageCta />
    </div>

    <!--
      The sidebar's call to action, now floating above the page: it stays within
      reach the whole way down the studio and gets out of the way once the
      closing CTA arrives. Hidden from print, and it sits above the stage only.
    -->
    <Transition name="floating-cta">
      <div
        v-show="showFloatingCta"
        class="sofax pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pr-20 print:hidden sm:pb-6 sm:pr-4"
      >
        <div
          class="pointer-events-auto mx-auto flex w-full max-w-3xl items-center justify-between gap-3 rounded-[0.625rem] bg-ink p-2 text-white shadow-lift sm:gap-4 sm:p-5 dark:bg-gray-800"
        >
          <!-- Just the button on a phone, so the bar doesn't sit over a quarter
               of the preview it is floating above. -->
          <div class="hidden min-w-0 text-left sm:block">
            <div class="font-title text-lg font-bold !text-white">{{ t('common.demo.cta.title') }}</div>
            <div class="!text-sm !text-white/70">{{ t('common.demo.cta.noCreditCard') }}</div>
          </div>

          <SofaxBtn
            to="/registration"
            variant="accent"
            class="btn-sm w-full sm:w-auto sm:shrink-0"
            :label="t('common.demo.cta.button')"
          />
        </div>
      </div>
    </Transition>
  </NuxtLayout>
</template>

<style>
@media print {
  body {
    background: white !important;
  }

  .print\:hidden {
    display: none !important;
  }

  .print\:shadow-none {
    box-shadow: none !important;
  }

  .print\:rounded-none {
    border-radius: 0 !important;
  }
}

/* Sideways scroll for the stage once the sheet is held at 100%. */
.preview-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}

/*
 * Swapping the stage: the outgoing layout fades down, the incoming one rises
 * into place, so switching format reads as one movement instead of a flash.
 */
.stage-enter-active,
.stage-leave-active {
  transition: opacity 250ms ease, transform 250ms cubic-bezier(0.22, 1, 0.36, 1);
}

.stage-enter-from {
  opacity: 0;
  transform: translateY(0.75rem);
}

.stage-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

/* The floating CTA slides out of the way rather than blinking off. */
.floating-cta-enter-active,
.floating-cta-leave-active {
  transition: opacity 250ms ease, transform 250ms cubic-bezier(0.22, 1, 0.36, 1);
}

.floating-cta-enter-from,
.floating-cta-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

@media (prefers-reduced-motion: reduce) {
  .floating-cta-enter-active,
  .floating-cta-leave-active {
    transition: none;
  }
}

/* Steady pulse next to the stage label: the preview is live, not a picture. */
.live-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: #0C9347;
  box-shadow: 0 0 0 0 rgba(12, 147, 71, 0.5);
  animation: demo-pulse 2s ease-out infinite;
}

@keyframes demo-pulse {
  70% {
    box-shadow: 0 0 0 0.5rem rgba(12, 147, 71, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(12, 147, 71, 0);
  }
}

/* The template zooms its doodles in on load. */
@media (prefers-reduced-motion: no-preference) {
  .demo-doodle {
    animation: demo-doodle-in 700ms cubic-bezier(0.34, 1.56, 0.64, 1) 200ms both;
  }
}

@keyframes demo-doodle-in {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}

@media (prefers-reduced-motion: reduce) {
  .stage-enter-active,
  .stage-leave-active {
    transition: none;
  }

  .live-dot {
    animation: none;
  }
}
</style>
